import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";

const RANGE_PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "7d", label: "Last 7 days", days: 6 },
  { id: "30d", label: "Last 30 days", days: 29 },
  { id: "custom", label: "Custom", days: null },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

function deriveRange(presetId, customSince, customUntil) {
  if (presetId === "custom") {
    return { since: customSince, until: customUntil };
  }
  const preset = RANGE_PRESETS.find((p) => p.id === presetId) || RANGE_PRESETS[2];
  const now = new Date();
  const until = isoDate(now);
  const start = new Date(now);
  start.setDate(now.getDate() - (preset.days || 0));
  return { since: isoDate(start), until };
}

const fmtCurrency = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;

function KPI({ label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-gray-200/70 bg-paper p-6 md:p-7"
    >
      <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600">
        {label}
      </p>
      <p className="mt-3 font-display text-[34px] md:text-[40px] leading-none tracking-tight text-ink">
        {value}
      </p>
      {sub && (
        <p className="mt-3 font-mono text-[11px] tracking-[0.18em] uppercase text-gray-500">
          {sub}
        </p>
      )}
    </div>
  );
}

function PlatformCard({ name, totals, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-gray-200/70 bg-paper p-7"
    >
      <div className="flex items-center justify-between mb-5">
        <p className="font-display text-2xl tracking-tight">{name}</p>
        <span
          className="font-mono text-[10px] tracking-[0.24em] uppercase px-2 py-0.5"
          style={{ background: accent, color: "#231F20" }}
        >
          {totals.impressions > 0 ? "Active" : "No data"}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Spend</dt>
          <dd className="mt-1 font-display text-2xl text-ink">{fmtCurrency(totals.spend)}</dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Conversions</dt>
          <dd className="mt-1 font-display text-2xl text-ink">{fmtInt(totals.conversions)}</dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Clicks</dt>
          <dd className="mt-1 text-ink">{fmtInt(totals.clicks)}</dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Impressions</dt>
          <dd className="mt-1 text-ink">{fmtInt(totals.impressions)}</dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">CTR</dt>
          <dd className="mt-1 text-ink">{fmtPct(totals.ctr)}</dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Avg CPC</dt>
          <dd className="mt-1 text-ink">{fmtCurrency(totals.cpc)}</dd>
        </div>
        <div className="col-span-2 pt-3 border-t border-gray-200/70">
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-600">Cost / Conversion</dt>
          <dd className="mt-1 font-display text-xl text-teal">{fmtCurrency(totals.cost_per_conversion)}</dd>
        </div>
      </dl>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toUpperCase();
  const active = s === "ACTIVE";
  const paused = s === "PAUSED";
  const cls = active
    ? "bg-teal/15 text-teal"
    : paused
    ? "bg-gray-300/50 text-gray-700"
    : "bg-gray-200/60 text-gray-600";
  return (
    <span className={`inline-block font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 ${cls}`}>
      {s || "—"}
    </span>
  );
}

function PlatformBadge({ platform }) {
  const isMeta = platform === "meta";
  const label = isMeta ? "Meta" : platform === "google" ? "Google" : platform;
  const cls = isMeta
    ? "bg-[#1877F2]/10 text-[#1877F2]"
    : "bg-[#4285F4]/10 text-[#4285F4]";
  return (
    <span className={`inline-block font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}

function campaignsToCSV(rows) {
  const headers = [
    "Platform",
    "Campaign",
    "Status",
    "Spend",
    "Clicks",
    "Impressions",
    "CTR (%)",
    "CPC",
    "Conversions",
    "Cost / Conv",
  ];
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.platform,
        r.campaign_name,
        r.status,
        r.spend,
        r.clicks,
        r.impressions,
        r.ctr,
        r.cpc,
        r.conversions,
        r.cost_per_conversion,
      ]
        .map(esc)
        .join(",")
    ),
  ];
  return lines.join("\n");
}

export default function AdminMarketing() {
  const [preset, setPreset] = useState("30d");
  const [customSince, setCustomSince] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return isoDate(d);
  });
  const [customUntil, setCustomUntil] = useState(() => isoDate(new Date()));
  const [summary, setSummary] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const { since, until } = useMemo(
    () => deriveRange(preset, customSince, customUntil),
    [preset, customSince, customUntil]
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { params: { since, until } };
      const [s, c, t] = await Promise.all([
        api.get("/admin/marketing/summary", params),
        api.get("/admin/marketing/campaigns", params),
        api.get("/admin/marketing/trend", params),
      ]);
      setSummary(s.data);
      setCampaigns(c.data || []);
      setTrend(t.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load marketing data.");
    } finally {
      setLoading(false);
    }
  }, [since, until]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSync = async () => {
    setSyncing(true);
    setError("");
    try {
      await api.post(`/admin/marketing/sync?since=${since}&until=${until}`);
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.detail || "Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const exportCSV = () => {
    const csv = campaignsToCSV(campaigns);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jos-marketing-${since}_to_${until}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = summary?.totals || {};
  const metaTotals = summary?.platforms?.meta || {};
  const googleTotals = summary?.platforms?.google || {};
  const lastSynced = summary?.last_synced || {};

  return (
    <div data-testid="admin-marketing-panel" className="space-y-10">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600 mb-3">
            Ads Performance
          </p>
          <h2 className="font-display text-[36px] md:text-[44px] leading-[1.05] tracking-tight">
            <span className="italic">{fmtCurrency(totals.spend)}</span> spent ·{" "}
            <span className="italic">{fmtInt(totals.conversions)}</span> conversions
          </h2>
          <p className="mt-3 font-mono text-[11px] tracking-[0.18em] uppercase text-gray-500">
            {since} → {until}
            {lastSynced.meta && (
              <>
                <span className="mx-2 text-gray-300">·</span>
                Last sync: {new Date(lastSynced.meta).toLocaleString()}
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2" data-testid="marketing-range-presets">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPreset(p.id)}
                data-testid={`range-preset-${p.id}`}
                className={
                  "font-mono text-[10px] tracking-[0.24em] uppercase border px-3 py-2 transition-colors " +
                  (preset === p.id
                    ? "bg-ink text-paper border-ink"
                    : "text-ink border-gray-300 hover:border-ink")
                }
              >
                {p.label}
              </button>
            ))}
          </div>

          {preset === "custom" && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customSince}
                max={customUntil}
                onChange={(e) => setCustomSince(e.target.value)}
                data-testid="marketing-custom-since"
                className="border-b border-gray-400/50 bg-transparent text-sm py-2 focus:outline-none focus:border-teal"
              />
              <span className="text-gray-500">→</span>
              <input
                type="date"
                value={customUntil}
                min={customSince}
                onChange={(e) => setCustomUntil(e.target.value)}
                data-testid="marketing-custom-until"
                className="border-b border-gray-400/50 bg-transparent text-sm py-2 focus:outline-none focus:border-teal"
              />
            </div>
          )}

          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            data-testid="marketing-sync-button"
            className="font-mono text-[10px] tracking-[0.24em] uppercase border border-teal bg-teal text-ink hover:bg-ink hover:text-paper hover:border-ink transition-colors px-4 py-2 disabled:opacity-60"
          >
            {syncing ? "Syncing…" : "Sync now"}
          </button>
          <button
            type="button"
            onClick={exportCSV}
            disabled={!campaigns.length}
            data-testid="marketing-export-csv"
            className="font-mono text-[10px] tracking-[0.24em] uppercase border border-ink text-ink hover:bg-ink hover:text-paper transition-colors px-4 py-2 disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <p data-testid="marketing-error" className="text-red-600 text-sm">
          {error}
        </p>
      )}
      {loading && !summary && (
        <p
          data-testid="marketing-loading"
          className="font-mono text-[11px] tracking-[0.24em] uppercase text-gray-600"
        >
          Loading metrics…
        </p>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI testId="kpi-spend" label="Total Spend" value={fmtCurrency(totals.spend)} />
        <KPI testId="kpi-clicks" label="Total Clicks" value={fmtInt(totals.clicks)} />
        <KPI
          testId="kpi-impressions"
          label="Impressions"
          value={fmtInt(totals.impressions)}
        />
        <KPI
          testId="kpi-conversions"
          label="Conversions"
          value={fmtInt(totals.conversions)}
        />
        <KPI
          testId="kpi-cpl"
          label="Cost / Lead"
          value={fmtCurrency(totals.cost_per_conversion)}
        />
        <KPI testId="kpi-ctr" label="Overall CTR" value={fmtPct(totals.ctr)} />
      </div>

      {/* Platform breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlatformCard
          testId="platform-meta"
          name="Meta Ads"
          totals={metaTotals}
          accent="#E3F6F5"
        />
        <PlatformCard
          testId="platform-google"
          name="Google Ads"
          totals={googleTotals}
          accent="#FFE9B0"
        />
      </div>

      {/* Trend chart */}
      <div data-testid="marketing-trend-chart" className="border border-gray-200/70 bg-paper p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="font-display text-2xl tracking-tight">Daily Spend</p>
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-500">
            One line per platform
          </p>
        </div>
        {trend.length === 0 ? (
          <p className="font-mono text-[11px] tracking-[0.24em] uppercase text-gray-500 py-10 text-center">
            No data in this range. Try a wider date range or click Sync now.
          </p>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="#e6e3df" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke="#6B6768"
                  fontSize={11}
                  tickFormatter={(d) => d?.slice(5)}
                />
                <YAxis
                  stroke="#6B6768"
                  fontSize={11}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  formatter={(v, name) => [fmtCurrency(v), name]}
                  contentStyle={{
                    background: "#FAF8F5",
                    border: "1px solid #ddd",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                <Line
                  type="monotone"
                  dataKey="meta_spend"
                  name="Meta"
                  stroke="#1877F2"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="google_spend"
                  name="Google"
                  stroke="#4285F4"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="6 4"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Campaign table */}
      <div className="border border-gray-200/70 bg-paper overflow-x-auto">
        <div className="px-6 py-4 border-b border-gray-200/70 flex items-center justify-between">
          <p className="font-display text-2xl tracking-tight">By Campaign</p>
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-500">
            {campaigns.length} campaigns
          </p>
        </div>
        <table data-testid="marketing-campaigns-table" className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-teal-light/30">
              {[
                "Platform",
                "Campaign",
                "Status",
                "Spend",
                "Clicks",
                "Impr.",
                "CTR",
                "CPC",
                "Conv.",
                "Cost/Conv",
              ].map((h) => (
                <th
                  key={h}
                  className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-gray-600 px-4 py-3 border-b border-gray-200/70"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center font-mono text-[11px] tracking-[0.24em] uppercase text-gray-500">
                  No campaign data in this range.
                </td>
              </tr>
            )}
            {campaigns.map((c, i) => (
              <tr
                key={`${c.platform}-${c.campaign_id}`}
                data-testid={`marketing-campaign-row-${i}`}
                className="border-b border-gray-200/60 hover:bg-teal-light/20"
              >
                <td className="px-4 py-3"><PlatformBadge platform={c.platform} /></td>
                <td className="px-4 py-3 text-ink">{c.campaign_name || c.campaign_id}</td>
                <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                <td className="px-4 py-3 text-ink whitespace-nowrap">{fmtCurrency(c.spend)}</td>
                <td className="px-4 py-3 text-gray-800">{fmtInt(c.clicks)}</td>
                <td className="px-4 py-3 text-gray-800">{fmtInt(c.impressions)}</td>
                <td className="px-4 py-3 text-gray-800">{fmtPct(c.ctr)}</td>
                <td className="px-4 py-3 text-gray-800">{fmtCurrency(c.cpc)}</td>
                <td className="px-4 py-3 text-ink">{fmtInt(c.conversions)}</td>
                <td className="px-4 py-3 text-teal whitespace-nowrap">{fmtCurrency(c.cost_per_conversion)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-400">
        Auto-syncs every 4 hours · Powered by Meta Marketing API v22.0
        {googleTotals.impressions > 0 ? " + Google Ads API" : " (Google Ads pending creds)"}
      </p>
    </div>
  );
}
